// Crear o editar una cita: titulo, dia y hora, duracion, lugar, enlace de la reunion, contacto, responsable y estado.
// Al crear se puede pedir una reunion de video de 3CX (D49): 3CX invita al contacto y el enlace queda en la cita.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { columna, filasDeArchivo } from '../lib/excel';
import { borrarCita, cambiarReunion3cx, type Cita, correoDeInvitacion, crearCita, crearReunion3cx, editarCita, esReunion3cx, ESTADOS_CITA, type EstadoCita, tomarCita } from '../api/citas';
import { buscarPersonas } from '../api/crm';
import { agentesVoz } from '../api/voz';
import { useSession } from '../auth/session';
import { useMiembros } from '../data/queries';
import { useDebounced, usePhone } from '../hooks';
import { Icon } from '../lib/icons';
import { ConEnlaces } from './Conversacion';
import { Dialog } from './Dialog';
import { SelectorFecha } from './SelectorFecha';
import { toast } from './toasts';

const dosDigitos = (n: number) => String(n).padStart(2, '0');
const fechaLocal = (d: Date) => `${d.getFullYear()}-${dosDigitos(d.getMonth() + 1)}-${dosDigitos(d.getDate())}`;
const horaLocal = (d: Date) => `${dosDigitos(d.getHours())}:${dosDigitos(d.getMinutes())}`;

export function CitaForm({ cita, inicio, personaId, personaNombre, empresaId, onClose }: { cita?: Cita; inicio?: string; personaId?: string; personaNombre?: string; empresaId?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { yo } = useSession();
  const miembros = useMiembros();
  const base = new Date(cita?.inicio ?? inicio ?? Date.now() + 86_400_000);
  if (!cita && !inicio) base.setHours(9, 0, 0, 0);
  const duracion = cita?.fin ? Math.round((new Date(cita.fin).getTime() - new Date(cita.inicio).getTime()) / 60_000) : 60;
  const [v, setV] = useState({
    titulo: cita?.titulo ?? (personaNombre ? `Reunión con ${personaNombre}` : ''),
    fecha: fechaLocal(base),
    hora: horaLocal(base),
    duracion: String(duracion),
    lugar: cita?.lugar ?? '',
    enlace: cita?.enlace ?? '',
    reunion3cx: false,
    tipo3cx: 'reunion' as 'reunion' | 'webinar',
    personaCorreo: '',
    estado: (cita?.estado ?? 'PROGRAMADA') as EstadoCita,
    responsableId: cita?.responsableId ?? yo.miembroId ?? '',
    notas: cita?.notas ?? '',
    personaId: cita?.personaId ?? personaId ?? '',
    personaNombre: cita?.persona?.nombre ?? personaNombre ?? '',
    empresaId: cita?.empresaId ?? empresaId ?? '',
  });
  const [buscar, setBuscar] = useState('');
  const q = useDebounced(buscar, 250);
  const personas = useQuery({ queryKey: ['buscar-personas', q], queryFn: () => buscarPersonas(q, 8), enabled: q.trim().length >= 2 });
  const [guardando, setGuardando] = useState(false);
  const [confirmar, setConfirmar] = useState(false);
  const agentes = useQuery({ queryKey: ['agentes-voz'], queryFn: agentesVoz, staleTime: 10 * 60_000, retry: false, enabled: !cita });
  // La extension que organiza la reunion: la del directorio de agentes y, si no aparece, la del telefono de esta
  // Consola (antes se avisaba "se usara la extension de tu telefono" pero no se enviaba y 3CX respondia sin extension).
  const telefono = usePhone();
  const extTelefono = telefono.config.modo === 'demo' ? '' : String(telefono.config.extension ?? '').replace(/\D/g, '');
  // Asistentes cargados desde Excel (reunion o webinar de 3CX): 3CX le envia la invitacion a cada uno.
  const [invitadosExcel, setInvitadosExcel] = useState<{ nombre: string; correo: string }[]>([]);
  const [errorExcel, setErrorExcel] = useState('');
  const cargarInvitados = async (archivo?: File) => {
    if (!archivo) return;
    setErrorExcel('');
    try {
      const [titulos = [], ...filas] = await filasDeArchivo(archivo);
      const iCorreo = columna(titulos, ['correo', 'email', 'e-mail', 'correo electronico']);
      const iNombre = columna(titulos, ['nombre', 'name', 'nombre completo']);
      if (iCorreo < 0) return setErrorExcel('El archivo debe tener una columna «Correo».');
      const vistos = new Set<string>();
      const lista = filas
        .map((f) => ({ nombre: iNombre >= 0 ? (f[iNombre] ?? '').trim() : '', correo: (f[iCorreo] ?? '').trim().toLowerCase() }))
        .filter((x) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x.correo) && !vistos.has(x.correo) && vistos.add(x.correo));
      if (!lista.length) return setErrorExcel('No encontré correos válidos en el archivo.');
      setInvitadosExcel(lista.slice(0, 500));
    } catch (err) {
      setErrorExcel(`No se pudo leer el archivo: ${(err as Error).message}`);
    }
  };
  const miExtension = agentes.data?.find((a) => a.email.toLowerCase() === (yo.email ?? '').toLowerCase())?.extension || extTelefono;

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    const ini = new Date(`${v.fecha}T${v.hora}:00`);
    const fin = new Date(ini.getTime() + Number(v.duracion || 60) * 60_000);
    setGuardando(true);
    try {
      const datos = { titulo: v.titulo, inicio: ini.toISOString(), fin: fin.toISOString(), lugar: v.lugar, enlace: v.enlace.trim(), estado: v.estado, responsableId: v.responsableId || null, notas: v.notas, personaId: v.personaId || null, empresaId: v.empresaId || null };
      if (cita) {
        // Reunion de 3CX (D97): primero alla (cancelar la borra; cambiar dia, hora, duracion o titulo la actualiza).
        const en3cx = await a3cx(datos);
        await editarCita(cita.id, datos);
        if (en3cx) toast(en3cx);
      } else if (v.reunion3cx) {
        // Primero la reunion en 3CX (si falla, no queda una cita a medias).
        const r = await crearReunion3cx({ titulo: v.titulo, inicio: ini.toISOString(), duracion: Number(v.duracion || 60), descripcion: v.notas, extension: miExtension, tipo: v.tipo3cx, invitados: [...(v.personaCorreo ? [{ nombre: v.personaNombre, correo: v.personaCorreo }] : []), ...invitadosExcel.filter((x) => x.correo !== v.personaCorreo)] });
        await crearCita({ ...datos, enlace: r.enlace || datos.enlace, origen: 'TRESCX', icsUid: r.icsUid });
      } else await crearCita(datos);
      void qc.invalidateQueries({ queryKey: ['citas'] });
      toast(cita ? 'Cita actualizada.' : 'Cita creada.');
      onClose();
    } catch (err) {
      toast((err as Error).message, 'error');
    } finally {
      setGuardando(false);
    }
  };
  // Lo que hay que llevar a 3CX al guardar una reunion de la central; devuelve el aviso para la persona (o null).
  const es3cx = !!cita && esReunion3cx(cita);
  const a3cx = async (d: { titulo: string; inicio: string; fin: string | null; estado: EstadoCita }) => {
    if (!cita || !es3cx) return null;
    const ref = { icsUid: cita.icsUid, enlace: cita.enlace };
    if (d.estado === 'CANCELADA' && cita.estado !== 'CANCELADA') {
      const r = await cambiarReunion3cx({ accion: 'borrar', ...ref });
      return r.enCentral ? 'También se canceló en 3CX: 3CX les avisa a los participantes.' : r.mensaje ?? null;
    }
    const minutos = d.fin ? Math.round((Date.parse(d.fin) - Date.parse(d.inicio)) / 60_000) : undefined;
    const cambio = d.titulo.trim() !== cita.titulo || Date.parse(d.inicio) !== Date.parse(cita.inicio) || minutos !== duracion;
    if (!cambio || d.estado === 'CANCELADA') return null;
    const r = await cambiarReunion3cx({ accion: 'cambiar', ...ref, titulo: d.titulo, inicio: d.inicio, duracion: minutos });
    return r.enCentral ? 'También se cambió en 3CX: 3CX les avisa a los participantes.' : r.mensaje ?? null;
  };
  const borrar = async () => {
    if (!cita) return;
    if (!confirmar) return setConfirmar(true);
    try {
      const r = es3cx && cita.estado !== 'CANCELADA' ? await cambiarReunion3cx({ accion: 'borrar', icsUid: cita.icsUid, enlace: cita.enlace }) : null;
      await borrarCita(cita.id);
      void qc.invalidateQueries({ queryKey: ['citas'] });
      toast(r?.enCentral ? 'Cita borrada y reunión cancelada en 3CX.' : 'Cita borrada.');
      onClose();
    } catch (err) {
      toast((err as Error).message, 'error');
    }
  };

  return (
    <Dialog titulo={cita ? 'Cita' : 'Nueva cita'} onClose={onClose} width={620}>
      <form className="form" onSubmit={guardar}>
        <label>
          Título
          <input id="ci-titulo" value={v.titulo} onChange={(e) => setV({ ...v, titulo: e.target.value })} required placeholder="Demostración de la Consola" />
        </label>
        <div className="grid3" style={{ gridTemplateColumns: 'minmax(0,1fr) 110px 130px' }}>
          <label>
            Día
            <SelectorFecha id="ci-fecha" value={v.fecha} onChange={(x) => setV({ ...v, fecha: x })} required etiqueta="Día de la cita" />
          </label>
          <label>
            Hora
            <input id="ci-hora" type="time" value={v.hora} onChange={(e) => setV({ ...v, hora: e.target.value })} required />
          </label>
          <label>
            Duración
            <select id="ci-dur" value={v.duracion} onChange={(e) => setV({ ...v, duracion: e.target.value })}>
              {[15, 30, 45, 60, 90, 120, 180, 240].map((m) => (
                <option key={m} value={m}>
                  {m < 60 ? `${m} min` : `${m / 60} h`}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="grid2">
          <label>
            Lugar
            <input id="ci-lugar" value={v.lugar} onChange={(e) => setV({ ...v, lugar: e.target.value })} placeholder="Oficina o dirección" />
          </label>
          <label>
            Enlace de la reunión
            <input id="ci-enlace" value={v.enlace} onChange={(e) => setV({ ...v, enlace: e.target.value })} placeholder="https://meet… / Teams / 3CX" disabled={v.reunion3cx} />
          </label>
        </div>
        {!cita ? (
          <label className="ci-check">
            <input type="checkbox" checked={v.reunion3cx} onChange={(e) => setV({ ...v, reunion3cx: e.target.checked })} />
            <span>
              Crear reunión de video en 3CX{v.personaCorreo ? ` e invitar a ${v.personaCorreo}` : ''}
              {v.reunion3cx ? <span className="sb">{miExtension ? ` · la organiza tu extensión ${miExtension}` : ' · configura tu teléfono (tu extensión) para poder crearla'}</span> : null}
            </span>
          </label>
        ) : cita.origen !== 'MANUAL' ? (
          <OrigenCita cita={cita} onTomada={(id) => setV({ ...v, responsableId: id })} onAbrirCaso={onClose} />
        ) : null}
        {!cita && v.reunion3cx ? (
          <label>
            Tipo de reunión
            <select id="ci-tipo3cx" value={v.tipo3cx} onChange={(e) => setV({ ...v, tipo3cx: e.target.value as 'reunion' | 'webinar' })}>
              <option value="reunion">Reunión de video (todos hablan)</option>
              <option value="webinar">Webinar (tú presentas; los asistentes entran en silencio)</option>
            </select>
            <span className="sb">
              <label className="btn sm" style={{ display: 'inline-flex', marginTop: 6 }}>
                {invitadosExcel.length ? `${invitadosExcel.length} invitados cargados · cambiar` : 'Cargar invitados desde Excel'}
                <input type="file" accept=".xlsx,.csv,text/csv" hidden onChange={(e) => void cargarInvitados(e.target.files?.[0])} />
              </label>{' '}
              Columnas Nombre y Correo (<a href="/formatos/invitados.xlsx" download>formato de ejemplo</a>); 3CX le envía la invitación a cada uno. Máximo 500.
            </span>
            {errorExcel ? <span className="error">{errorExcel}</span> : null}
          </label>
        ) : null}
        <label>
          Contacto
          {v.personaId ? (
            <span className="ci-persona">
              {v.personaNombre || 'Contacto'}{' '}
              <button type="button" className="linkish" onClick={() => setV({ ...v, personaId: '', personaNombre: '' })}>
                cambiar
              </button>
            </span>
          ) : (
            <>
              <input id="ci-buscar" value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Busca por nombre, correo o número" autoComplete="off" />
              {personas.data?.items?.length ? (
                <div className="ci-opciones">
                  {personas.data.items.map((p) => (
                    <button key={p.id} type="button" onClick={() => (setV({ ...v, personaId: p.id, personaNombre: p.nombreCompleto, personaCorreo: p.email ?? '', empresaId: p.empresa?.id ?? v.empresaId }), setBuscar(''))}>
                      {p.nombreCompleto} <span className="sb">{p.empresa?.nombre ?? p.email}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </label>
        <div className="grid2">
          <label>
            Responsable
            <select id="ci-resp" value={v.responsableId} onChange={(e) => setV({ ...v, responsableId: e.target.value })}>
              <option value="">Sin responsable</option>
              {(miembros.data ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nombre}
                </option>
              ))}
            </select>
          </label>
          <label>
            Estado
            <select id="ci-estado" value={v.estado} onChange={(e) => setV({ ...v, estado: e.target.value as EstadoCita })}>
              {ESTADOS_CITA.map(([e, t]) => (
                <option key={e} value={e}>
                  {t}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label>
          Notas
          <textarea id="ci-notas" rows={3} value={v.notas} onChange={(e) => setV({ ...v, notas: e.target.value })} />
        </label>
        {es3cx ? (
          <p className="note" style={{ margin: 0 }}>
            Es una reunión de 3CX: si cambias el día, la hora, la duración o el título, o la cancelas o la borras, también se hace en 3CX, que les avisa a los participantes.
          </p>
        ) : null}
        <div className="actions">
          {cita ? (
            <button type="button" className={`btn ${confirmar ? 'danger' : 'ghost'}`} onClick={borrar} style={{ marginRight: 'auto' }}>
              {confirmar ? (es3cx ? 'Sí, borrar y cancelar en 3CX' : 'Sí, borrar') : 'Borrar'}
            </button>
          ) : null}
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn primary" disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

// De donde salio una cita que no se puso a mano (D49): a que buzon llego la invitacion, quien la organiza y de quien es;
// con el correo original, el caso que abrio y "Lo tomo" si nadie la tiene.
function OrigenCita({ cita, onTomada, onAbrirCaso }: { cita: Cita; onTomada: (miembroId: string) => void; onAbrirCaso: () => void }) {
  const { yo } = useSession();
  const miembros = useMiembros();
  const qc = useQueryClient();
  const ir = useNavigate();
  const [ver, setVer] = useState(false);
  const correo = useQuery({ queryKey: ['invitacion', cita.invitacionId], queryFn: () => correoDeInvitacion(cita.invitacionId!), enabled: ver && !!cita.invitacionId, retry: false, staleTime: 10 * 60_000 });
  const [responsable, setResponsable] = useState(cita.responsableId);
  const nombre = (id: string | null) => (id === yo.miembroId ? 'tú' : miembros.data?.find((m) => m.id === id)?.nombre ?? 'otra persona');
  const tomar = async () => {
    if (!yo.miembroId) return;
    try {
      await tomarCita(cita.id, yo.miembroId);
      setResponsable(yo.miembroId);
      onTomada(yo.miembroId);
      void qc.invalidateQueries({ queryKey: ['citas'] });
      toast('La cita ahora es tuya.');
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  };
  return (
    <div className="ci-origen">
      <div className="ci-origen-l">
        <Icon name={cita.origen === 'TRESCX' ? 'headset' : 'mail'} size={14} />
        <span>
          {cita.origen === 'TRESCX' ? 'Reunión de 3CX' : 'Invitación'}
          {cita.buzon ? (
            <>
              {' '}
              que llegó a <b>{cita.buzon}</b>
            </>
          ) : null}
          {cita.organizador ? ` · organiza ${cita.organizador}` : ''}
          {' · '}
          {responsable ? (
            <>
              es de <b>{nombre(responsable)}</b>
            </>
          ) : (
            <b className="ag-sintomar">nadie la ha tomado</b>
          )}
          . Si la cambian allá, se actualiza sola.
        </span>
      </div>
      <div className="ci-origen-acc">
        {cita.invitacionId ? (
          <button type="button" className="btn sm" onClick={() => setVer(!ver)}>
            <Icon name="mail" size={12} /> {ver ? 'Ocultar la invitación' : 'Ver la invitación'}
          </button>
        ) : null}
        {cita.casoId ? (
          <button type="button" className="btn sm" onClick={() => (onAbrirCaso(), ir(`/bandeja/casos?caso=${cita.casoId}`))}>
            <Icon name="external" size={12} /> Abrir el caso
          </button>
        ) : null}
        {!responsable && yo.miembroId ? (
          <button type="button" className="btn sm primary" onClick={() => void tomar()}>
            Lo tomo
          </button>
        ) : null}
      </div>
      {ver ? (
        correo.isLoading ? (
          <p className="sub">Trayendo el correo del buzón…</p>
        ) : correo.error ? (
          <p className="sub">{(correo.error as Error).message}</p>
        ) : correo.data ? (
          <div className="ci-correo">
            <div className="sb">
              De: {correo.data.de}
              <br />
              Para: {correo.data.para}
              {correo.data.cc ? (
                <>
                  <br />
                  CC: {correo.data.cc}
                </>
              ) : null}
              <br />
              {new Date(correo.data.fecha).toLocaleString('es-CO')} · {correo.data.asunto}
            </div>
            <div className="conv-t">
              <ConEnlaces texto={correo.data.texto || '(sin texto)'} />
            </div>
          </div>
        ) : null
      ) : null}
    </div>
  );
}
